import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Pricing.css";

const formatExpiry = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const Pricing = () => {
  const navigate = useNavigate();
  const [activePlanId, setActivePlanId] = useState(null);
  const [expiresLabel, setExpiresLabel] = useState("");
  const [activatingPlanId, setActivatingPlanId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchSubscription = async () => {
      try {
        const res = await axios.get("/api/auth/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = res.data?.user;
        if (user?.subscriptionPlan && user?.subscriptionExpiresAt) {
          setActivePlanId(user.subscriptionPlan);
          setExpiresLabel(formatExpiry(user.subscriptionExpiresAt));
        }
      } catch (err) {
        console.error("Failed to load subscription status:", err);
      }
    };

    fetchSubscription();
  }, []);

  const handleClaim = async (planId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Please log in to claim this offer");
      navigate("/login");
      return;
    }

    setActivatingPlanId(planId);
    try {
      const res = await axios.post(
        "/api/auth/activate-plan",
        { planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const subscription = res.data?.subscription;
      setActivePlanId(subscription?.plan || planId);
      setExpiresLabel(formatExpiry(subscription?.expiresAt));
      toast.success("Plan activated successfully");
    } catch (err) {
      console.error("Plan activation error:", err);
      toast.error(err?.response?.data?.msg || "Failed to activate plan");
    } finally {
      setActivatingPlanId(null);
    }
  };

  const plans = [
    {
      id: "starter",
      name: "1 Month",
      originalPrice: "Rs 100",
      discountPrice: "Rs 0",
      term: "1 month",
      tagline: "Great for quick legal checks",
      features: [
        "Full access to legal solutions",
        "Public-level explanations",
        "Search history & saved results",
        "Email support",
      ],
      cta: "Claim Launch Offer",
    },
    {
      id: "growth",
      name: "6 Months",
      originalPrice: "Rs 450",
      discountPrice: "Rs 0",
      term: "6 months",
      tagline: "Best value for steady use",
      featured: true,
      features: [
        "Everything in Starter",
        "Priority search speed",
        "Draft creation tools",
        "WhatsApp support",
      ],
      cta: "Claim Launch Offer",
    },
    {
      id: "pro",
      name: "1 Year",
      originalPrice: "Rs 800",
      discountPrice: "Rs 0",
      term: "1 year",
      tagline: "Built for long-term savings",
      features: [
        "Everything in Growth",
        "Advanced legal summaries",
        "Premium advisor matching",
        "Early access to new features",
      ],
      cta: "Claim Launch Offer",
    },
  ];

  const policies = [
    {
      title: "Straightforward billing",
      detail: "All plans are prepaid. No hidden charges. Prices are in INR.",
    },
    {
      title: "Cancel anytime",
      detail: "You can cancel auto-renewal in your profile. Access remains until plan ends.",
    },
    {
      title: "Secure payments",
      detail: "Payments are handled by trusted gateways with encrypted transactions.",
    },
    {
      title: "Fair usage",
      detail: "Unlimited searches for personal use. Abuse protection applies to keep service fast.",
    },
  ];

  return (
    <div className="pricing-page">
      <header className="pricing-hero">
        <div className="pricing-hero-inner">
          <p className="pricing-kicker">Transparent Pricing</p>
          <div className="pricing-launch">
            <span className="launch-pill">Launching Offer</span>
            <strong>100% OFF</strong>
            <span className="launch-note">Limited time access for everyone</span>
          </div>
          <h1>Pick a plan that grows with your legal needs</h1>
          <p className="pricing-subtitle">
            Simple monthly options, smarter long-term savings. Choose a plan and get
            instant access to legal solutions, drafting tools, and expert guidance.
          </p>
          <div className="pricing-highlights">
            <span>Instant access</span>
            <span>Secure payments</span>
            <span>Cancel anytime</span>
          </div>
        </div>
      </header>

      <section className="pricing-grid">
        {plans.map((plan, index) => {
          const isClaimable = plan.id === "growth";
          const isActivated = activePlanId === plan.id;
          const isActivating = activatingPlanId === plan.id;
          return (
          <article
            key={plan.id}
            className={`pricing-card ${plan.featured ? "featured" : ""}`}
            style={{ animationDelay: `${index * 120}ms` }}
          >
            <div className="pricing-card-head">
              <h2>{plan.name}</h2>
              <p className="pricing-tagline">{plan.tagline}</p>
            </div>
            <div className="pricing-amount">
              <span className="pricing-price">{plan.discountPrice}</span>
              <span className="pricing-original">{plan.originalPrice}</span>
              <span className="pricing-term">/{plan.term}</span>
            </div>
            <ul className="pricing-features">
              {plan.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            {isActivated ? (
              <div className="pricing-activated">
                Plan Activated till {expiresLabel}
              </div>
            ) : (
              <button
                className={`pricing-cta ${isClaimable ? "" : "pricing-cta-disabled"}`}
                onClick={() => isClaimable && handleClaim(plan.id)}
                disabled={!isClaimable || isActivating}
              >
                {isActivating ? "Activating..." : plan.cta}
              </button>
            )}
          </article>
          );
        })}
      </section>

      <section className="pricing-policy">
        <div className="policy-header">
          <h2>Pricing policies</h2>
          <p>
            We keep pricing clear and predictable so you can focus on getting the
            right legal help.
          </p>
        </div>
        <div className="policy-grid">
          {policies.map((policy) => (
            <div key={policy.title} className="policy-card">
              <h3>{policy.title}</h3>
              <p>{policy.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing-footer">
        <div className="pricing-footer-card">
          <h2>Need help choosing a plan?</h2>
          <p>
            Talk to our team and we will recommend the right subscription based on
            your needs.
          </p>
          <button className="pricing-secondary">Contact support</button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
